"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import Image from "next/image";
import React, { useMemo, useRef } from "react";

export interface Faculty5MetaItem {
  id?: string | number;
  priority?: number | string;
  status?: boolean;
  metaImg?: string;
  altImg?: string;
  metaTitle?: string;
  metaDescription?: string;
  companyLogo1?: string;
  companyLogo2?: string;
  companyLogo3?: string;
}

export interface Faculty5Data {
  id?: string;
  status?: boolean;
  title?: string;
  title_text_color?: string;
  title_1_color?: string;
  description?: string;
  description_text_color?: string;
  background_color?: string;
  background_image?: string;
  background_image_alt?: string;
  metadata?: Faculty5MetaItem[];
}

interface Faculty5Props {
  data: Faculty5Data | { data: Faculty5Data };
}

function resolvePayload(data: Faculty5Props["data"]): Faculty5Data | undefined {
  const anyData = data as { data?: Faculty5Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as Faculty5Data;
  }
  return data as Faculty5Data;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** `{highlight}` / `{{line}}` → span with admin-chosen highlight color (inline
 *  style so arbitrary hex values bypass Tailwind JIT). */
function formatTitleWithHighlight(title: string, highlightColor: string): string {
  if (!title) return "";
  const safeColor = highlightColor.replace(/[^#a-zA-Z0-9(),.%\s]/g, "");
  const openTag = `<span style="color:${safeColor}">`;

  if (title.includes("{{")) {
    return title
      .replace(/\{\{([^}]+)\}\}/g, (_m, inner) => `${openTag}${escapeHtml(inner)}</span><br/>`)
      .replace(/\s+/g, " ");
  }

  if (title.includes("{")) {
    return title
      .replace(/\{/g, openTag)
      .replace(/\}/g, "</span>")
      .replace(/\s+/g, " ");
  }

  return escapeHtml(title);
}

const Faculty5 = ({ data }: Faculty5Props) => {
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);

  const {
    status = true,
    title = "",
    title_text_color = "#1A1A1A",
    title_1_color = "#E36E26",
    description = "",
    description_text_color = "#1A1A1A",
    background_color = "#FFFFFF",
    background_image = "",
    background_image_alt = "",
    metadata = [],
  } = sectionData || {};

  const items = useMemo(() => {
    if (!Array.isArray(metadata) || metadata.length === 0) return [];
    return [...metadata]
      .filter((m) => m && (m.status === undefined || m.status === true))
      .sort((a, b) => Number(a.priority ?? 0) - Number(b.priority ?? 0));
  }, [metadata]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status && items.length > 0) {
  //     FireWidgetViewedEvent(ref, "faculty_5");
  //   }
  // }, [status, items.length]);

  if (!status || items.length === 0) return null;

  return (
    <section
      ref={ref}
      data-faculty="5"
      className={cn(
        "relative flex w-full items-center justify-center overflow-hidden",
        "px-7 pt-11 pb-16 min-[768px]:py-10",
      )}
      style={{ backgroundColor: background_color }}
    >
      {/* Background image — full-bleed across the whole widget */}
      {background_image ? (
        <img
          src={background_image}
          alt={background_image_alt || ""}
          className="absolute inset-0 -z-10 h-full w-full object-cover object-center"
        />
      ) : null}

      <div className="relative z-10 mx-auto w-full max-w-screen-container">
        <div className="relative flex w-full flex-col items-center justify-center gap-[30px] py-2.5">
          {/* Title + Description */}
          <div className="flex w-[320px] flex-col items-center justify-center gap-3.5 text-center min-[768px]:w-[766px] min-[768px]:gap-1.5">
            {title ? (
              <h2
                className="font-poppins text-[28px] font-semibold leading-tight min-[768px]:text-[32px]"
                style={{ color: title_text_color }}
                dangerouslySetInnerHTML={{
                  __html: formatTitleWithHighlight(title, title_1_color),
                }}
              />
            ) : null}
            {description ? (
              <p
                className="text-[14px] min-[768px]:text-[18px]"
                style={{ color: description_text_color }}
              >
                {description}
              </p>
            ) : null}
          </div>

          {/* Leader cards */}
          <div
            className={cn(
              "flex w-full flex-col flex-wrap items-center justify-center gap-[38px] overflow-auto",
              "min-[768px]:flex-row min-[768px]:px-16",
            )}
          >
            {items.map((item, index) => (
              <LeaderCard key={item.id ?? `card-${index}`} item={item} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

interface LeaderCardProps {
  item: Faculty5MetaItem;
}

const LeaderCard = ({ item }: LeaderCardProps) => {
  const {
    metaImg = "",
    altImg = "",
    metaTitle = "",
    metaDescription = "",
    companyLogo1 = "",
    companyLogo2 = "",
    companyLogo3 = "",
  } = item;

  const companies = [companyLogo1, companyLogo2, companyLogo3].filter(Boolean);

  return (
    <div className="min-h-[332px] min-w-[276px] max-w-[280px] shadow-[0_4px_24px_0_rgba(0,0,0,0.12)]">
      <div className="relative flex h-[156px] items-center justify-center bg-primary">
        {metaImg ? (
          <Image src={metaImg} fill alt={altImg || metaTitle} className="object-cover" />
        ) : null}
      </div>
      <div className="flex flex-col gap-3.5 bg-white py-4 px-[18px]">
        <div className="flex flex-col gap-1.5 text-center">
          <p className="text-[16px] font-semibold min-[768px]:text-[18px]">{metaTitle}</p>
          {metaDescription ? (
            <p className="text-[14px] text-black">{metaDescription}</p>
          ) : null}
        </div>
        {companies.length > 0 ? (
          <div className="h-px w-full bg-gray-3" />
        ) : null}
        <div className="flex h-[60px] justify-center gap-4 overflow-hidden">
          {companies.map((logo, index) => (
            <React.Fragment key={index}>
              {index > 0 ? (
                <div className="w-px self-stretch bg-gray-3" />
              ) : null}
              <div className="relative h-[60px] w-[114px]">
                <Image src={logo} alt="" fill className="object-contain" />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Faculty5;
